<template>
  <div
    class="formField selectField"
    :class="{
      hasFocus,
      hasValue,
      isError,
      isDisabled,
      isInline,
      isSmall,
      hasAppend,
      isAutocomplete,
      hasTooltip: formLabelProps.hasTooltip,
      withoutBorder,
    }"
    v-click-outside="() => setFocus(false)"
  >
    <FormLabel v-bind="formLabelProps" :class="{ isHidden: !((!hasFocus && isAutocomplete) || !isAutocomplete) }" />
    <div
      class="selectField__Wrapper"
      :tabindex="!isDisabled ? 0 : -1"
      @click.stop="toggleFocus"
    >
      <div class="selectField__Prepend" v-if="$slots.prepend">
        <slot name="prepend" />
      </div>
      <Tooltip
        :text="formLabelProps.tooltip"
        :click-only="formLabelProps.tooltipClickOnly"
        v-if="formLabelProps.hasTooltip"
      >
        <slot name="tooltip" />
      </Tooltip>
      <input
        :id="uniqueID"
        :required="isRequired"
        :name="name"
        type="text"
        :value="selectText"
        :placeholder="placeholder"
        :readonly="true"
        :tabindex="-1"
        aria-label="Selected value"
        v-if="!isAutocomplete || (isAutocomplete && !hasFocus)"
      />
      <select
        :required="isRequired"
        :tabindex="-1"
        :value="selectText"
        :multiple="isMultiple"
        @change="setSelectValue"
        aria-label="Selected value"
        v-if="!isAutocomplete || (isAutocomplete && !hasFocus)"
      >
        <option v-for="item in getOptions" :value="item[valueKey]" v-text="item[textKey]" />
      </select>
      <input
        class="selectField__Search"
        ref="autocompleteInput"
        type="text"
        :placeholder="$t('search')"
        :tabindex="!isDisabled ? 0 : -1"
        v-model="search"
        v-if="isAutocomplete && hasFocus"
      />
      <div class="selectField__Append" v-if="$slots['append']">
        <slot name="append" />
      </div>
      <div class="selectField__Options">
        <ul :class="{ noResults: getOptions?.length === 0 }">
          <li
            v-for="item in getOptions"
            @click.stop="setSingleValue(item)"
            :class="{ isSelected: isSelected(item), isDisabled: item.disabled }"
            :tabindex="!isDisabled ? 0 : -1"
            @keydown="handleKeyDown"
          >
            <CheckboxField tabindex="-1" v-model="fieldValue" :value="item" :label="item[textKey]" v-if="isMultiple" />
            <template v-else>
              <slot name="option" v-bind="{ item }">
                {{ item[textKey] }}
              </slot>
            </template>
          </li>
          <li v-if="getOptions?.length === 0">
            {{ $t('noResults') }}
          </li>
        </ul>
      </div>
    </div>
    <FormError :errors="mergedErrors" className="selectField__Error" />
    <span class="selectField__Info form__Info" v-html="info" v-if="info" />
  </div>
</template>

<script setup>
  const emit = defineEmits(['update:modelValue', 'change', 'focus'])
  const props = defineProps({
    ...useFormFieldProps(),
    value: {},
    placeholder: {
      type: String
    },
    options: {
      type: Array
    },
    textKey: {
      type: String,
      default: 'title'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    withoutBorder: {
      type: Boolean,
      default: false
    },
  })

  const { isMobile } = useSetup()
  const {
    isError,
    isDisabled,
    isRequired,
    isInline,
    hasValue,
    mergedErrors,
    uniqueID,
    formLabelProps,
    hasFocus,
    setFocus,
    triggerValidate
  } = useFormField(props, emit)

  const search = ref('')
  const selectText = ref('')
  const autocompleteInput = ref(null)

  const isMultiple = computed(() => props.multiple !== false)
  const isAutocomplete = computed(() => props.autocomplete !== false)
  const isSmall = computed(() => props.small !== false)
  const hasAppend = computed(() => useSlots().append !== undefined)

  const getOptions = computed(() => {
    let options = props.options
    if (props?.options?.find(i => (typeof i).toLowerCase() !== 'object')) {
      options = props.options.map(i => ({
        [props.textKey]: i,
        [props.valueKey]: i
      }))
    }

    if (isAutocomplete.value) {
      options = options.filter(i => i[props.textKey]?.toString().toLowerCase()?.includes(search.value.toLowerCase()))
    }

    return options
  })

  const setText = () => {
    if (props.value) selectText.value = props.value
    else if (isMultiple.value) selectText.value = fieldValue.value.map(i => i[props.textKey]).join(', ')
    else selectText.value = fieldValue.value?.[props.textKey] || fieldValue.value
    if(isMobile.value) {
      selectText.value = getOptions.value.find(i => i[props.valueKey] == fieldValue.value)?.[props.textKey] || ''
    }
  }

  function setSelectValue(e){
    setFocus(false)
    fieldValue.value = e.target.value;
  }

  function setValue(){
    if(!isMultiple.value) {
      setSingleValue(getOptions.value.find(i => props.modelValue == i[props.valueKey]) || props.modelValue)
    }
  }

  const setSingleValue = (value) => {
    if (!value?.disabled && !isMultiple.value) {
      value = value || ''
      fieldValue.value = value
      if (isMobile.value) fieldValue.value = value[props.valueKey] || ''
      setFocus(false)
    }

    if(props.value) emit('change', value[props.valueKey])
  }

  const isSelected = option => {
    if (!isMultiple.value) return props.modelValue == option[props.valueKey]
  }

  const getInitialValue = () => {
    if(props.value) return props.value
    if (isMultiple.value) return getOptions.value.filter(i => props.modelValue.includes(i[props.valueKey])) || []
    try {
      return getOptions.value.find(i => i[props.valueKey] == props.modelValue) || ''
    } catch (e) {
      return ''
    }
  }

  const handleKeyDown = e => {
    if ([0, 13, 32].includes(e.keyCode) && isDisabled.value === false) {
      e.preventDefault()
      e.target.closest('li').click()
      try {
        e.target.querySelector('[type="checkbox"]').click()
      } catch (e) {}
    }
  }

  function toggleFocus(){
    if(hasFocus.value) setFocus(false)
    else setFocus(true)
  }

  const fieldValue = ref(getInitialValue())

  watch(fieldValue, () => {
    if(props.value) return props.value
    let value = fieldValue.value
    if(!isMultiple.value) {
      if(fieldValue.value?.[props.valueKey] !== undefined) value = fieldValue.value?.[props.valueKey]
      if(value === undefined) value = fieldValue.value
    } else {
      value = fieldValue.value.map(i => i?.[props.valueKey] || i)
    }

    setText()
    emit('update:modelValue', value)
  })

  watch(() => props.modelValue, () => {
    setValue()
  })

  watch(() => props.value, () => {
    setText()
  })

  watch(hasFocus, () => {
    if (isAutocomplete.value) {
      if (hasFocus.value) nextTick(() => autocompleteInput.value.focus())
      else {
        setTimeout(() => {
          search.value = ''
        }, 300)
      }
    }

    if (!hasFocus.value) {
      emit('focus', false)
    } else {
      emit('focus', true)
    }
  })

  if(isMobile.value) setSingleValue(fieldValue.value)

  onMounted(() => {
    setText()
  })

  defineExpose({
    triggerValidate,
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/components/forms/_selectField.scss');
</style>